import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { FieldArray, Form, Formik } from 'formik';
import * as yup from 'yup';
import { createLoadHoc } from '../../../../helpers/hocs/createLoadHoc';
import {
    selectGetMobileBlocksLoader,
    selectMobileBodyBlock,
} from '../../../../selectors/mainPage';
import {
    BannerItemForm,
    BlockItemEdit,
    MobileBlockKey,
} from '../../../../types/Models/MainPage/MainPageModel';
import { units } from '../../../../helpers/styles/units';
import { FontWeight } from '../../../../constants/Styles/fontWeight';
import { useTypeSelector } from '../../../../store';
import { PageTitle } from '../../../../components/PageTitle';
import { Breadcrumbs } from '../../../../components/Breadcrumbs';
import { UploadPhotoWithMiniature } from '../../../../components/UploadPhotoWithMiniature';
import { typography } from '../../../../helpers/styles/typography';
import { colors } from '../../../../constants/Colors';
import { Input } from '../../../../components/Input';
import { editBannerItemsMobile } from '../../../../actions/mainPage';
import { getShotPath } from '../../../../helpers/images/getPath';
import { UploadFile } from '../../../../types/Endpoints/files/FilesContract';
import { mobileBodyBreadcrumbs } from '../../../../constants/BreadCrumbs/mobileBodybreadcrumbs';
import { SubmitButtons } from '../../../../components/StyledComponents';
import { isEqualObject } from '../../../../helpers/isEqual';
import { addNotification } from '../../../../actions/notifications';
import { TypesSnackbars } from '../../../../types/TypesSnackbars';
import { TypesIcon } from '../../../../types/TypesIcon';

const StyledRootWrapper = styled.div`
  position: relative;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-bottom: ${units(63)};
`;

const StyledMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(30)};

  max-width: ${units(322)};
  width: 100%;
`;

const StyledInput = styled(Input)`
  margin-bottom: 0;
  font-weight: ${FontWeight.REGULAR};
  letter-spacing: 0.8px;

  background-color: ${colors.light};

  ${typography(10)};
`;

const StyledInpurWrapper = styled.div`
  margin-bottom: ${units(12)};
`;

const StyledForm = styled(Form)`
  width: 100%;
  padding-top: ${units(20)};
`;

const StyledUploader = styled(UploadPhotoWithMiniature)`
  width: 100%;
`;

const SubmitButtonsWrapper = styled.div`
  margin-left: -${units(12)};
`;

interface CategoryBanner {
  id: number;
  key: string;
  name: string;
  platform: 'WEB';
  title: string | null;
  type: string;
  url: string | null;
  fileId: number | null;
  fileURL: string | null;
  fileImage: UploadFile;
  bannerItems: BannerItemForm[];
}

export const CategoriesBannerContent = (): JSX.Element => {
    const dispatch = useDispatch();

    const siteBodyBlock = useTypeSelector(
        selectMobileBodyBlock(MobileBlockKey.MOBILE_CATEGORIES),
    );

    const handleSubmit = (values: any) => {
        const requestData = {
            bannerItems: values.bannerItems.map(
                (item: BannerItemForm, index: number) => ({
                    buttonText: item.buttonText || null,
                    title: item.title || null,
                    text: item.text || null,
                    url: item.url || null,
                    fileId: item.fileImage?.id || null,
                    status: item.status || 'ACTIVE',
                    order: item.order || index + 1,
                    endDate: item.endDate || null,
                    startDate: item.startDate || null,
                }),
            ),
        };

        dispatch(
            editBannerItemsMobile({
                key: MobileBlockKey.MOBILE_CATEGORIES,
                newData: requestData.bannerItems,
            }),
        );
    };

    const items = siteBodyBlock?.bannerItems.map(item => ({
        ...item,
        fileImage: {
            id: item?.fileId || 0,
            path: item.fileURL ? getShotPath(item.fileURL) : '',
        },
    }));

    const validationSchema = yup.object().shape({
        bannerItems: yup.array().of(
            yup.object().shape({
                title: yup.string().required('Поле является обязательным'),
                url: yup.string().required('Поле является обязательным'),
            }),
        ),
    });

    const initialValue: BlockItemEdit<MobileBlockKey> = {
        buttonText: siteBodyBlock?.buttonText || '',
        title: siteBodyBlock?.title || '',
        text: siteBodyBlock?.text || '',
        url: siteBodyBlock?.url || '',
        name: siteBodyBlock?.name || '',
        key: MobileBlockKey.MOBILE_CATEGORIES,
        platform: 'MOBILE',
        type: siteBodyBlock?.type || 'TILE',
        bannerItems: items || [],
        bannerProducts: siteBodyBlock?.bannerProducts || [],
        fileURL: siteBodyBlock?.fileURL || '',
        id: siteBodyBlock?.id || 0,
        fileImage: {
            id: siteBodyBlock?.fileId || 0,
            path: siteBodyBlock?.fileURL ? getShotPath(siteBodyBlock.fileURL) : '',
        },
    };

    const handleResetForm = (calb: () => void, values: CategoryBanner) => {
        calb();
        const equal = isEqualObject(initialValue, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    return (
        <StyledRootWrapper>
            <StyledWrapper>
                <PageTitle>Body мобильного приложения</PageTitle>
                <Breadcrumbs breadcrumbs={mobileBodyBreadcrumbs()} />
                <Formik<CategoryBanner>
                    initialValues={initialValue as unknown as CategoryBanner}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {props => (
                        <StyledForm onSubmit={handleSubmit}>
                            <FieldArray name="bannerItems">
                                {() => (
                                    <StyledMainWrapper>
                                        {props.values.bannerItems?.map(
                                            (_block: BannerItemForm, index: number) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <div key={`block${index}`}>
                                                    <StyledInpurWrapper>
                                                        <StyledInput
                                                            name={`bannerItems[${index}].title`}
                                                            onChange={props.handleChange}
                                                            value={props.values?.bannerItems[index]?.title}
                                                            title="Заголовок*"
                                                            placeholder="Заголовок*"
                                                            typeInput="text"
                                                            maxLength={255}
                                                            isTouched={
                                                                props.touched?.bannerItems
                                                                && props.touched?.bannerItems[index]?.title
                                                            }
                                                            error={
                                                                props.errors?.bannerItems
                                                                && props.errors?.bannerItems[index]?.title
                                                            }
                                                            count={props.submitCount}
                                                        />
                                                    </StyledInpurWrapper>
                                                    <StyledInpurWrapper>
                                                        <StyledInput
                                                            name={`bannerItems[${index}].url`}
                                                            onChange={props.handleChange}
                                                            value={props.values?.bannerItems[index]?.url}
                                                            title="URL*"
                                                            placeholder="URL*"
                                                            typeInput="text"
                                                            isTouched={
                                                                props.touched?.bannerItems
                                                                && props.touched?.bannerItems[index]?.url
                                                            }
                                                            error={
                                                                props.errors?.bannerItems
                                                                && props.errors?.bannerItems[index]?.url
                                                            }
                                                            count={props.submitCount}
                                                        />
                                                    </StyledInpurWrapper>

                                                    <StyledUploader
                                                        name={`bannerItems[${index}].fileImage`}
                                                        onChange={props.setFieldValue}
                                                        value={props.values.bannerItems[index].fileImage}
                                                        maxSize={2}
                                                    />
                                                </div>
                                            ),
                                        )}
                                    </StyledMainWrapper>
                                )}
                            </FieldArray>
                            <SubmitButtonsWrapper>
                                <SubmitButtons
                                    reset={() => handleResetForm(props.resetForm, props.values)}
                                    submit={props.submitForm}
                                />
                            </SubmitButtonsWrapper>
                        </StyledForm>
                    )}
                </Formik>
            </StyledWrapper>
        </StyledRootWrapper>
    );
};

const loader = createLoadHoc({
    selectLoader: selectGetMobileBlocksLoader,
});

export const CategoriesBannerWithLoader = loader(CategoriesBannerContent);
